import { gql } from "@apollo/client";
import jwtDecode from "jwt-decode";
import Router, { useRouter } from "next/router";
import { useEffect, createContext, useCallback, useReducer } from "react";

import { AccessToken, Groups } from "src/auth/types/userToken";
import {
  consumePendingOAuthRedirectPath,
  extractPracticeIdFromPath,
  isSafeInternalRedirectPath,
  peekPendingOAuthRedirectPath,
  resolvePostLoginRedirectPath,
} from "src/auth/utils/postLoginRedirect";
import {
  getStoredPracticeId,
  setLocalPracticeId,
  setStoredPracticeId,
} from "src/auth/utils/practice";
import { refreshToken } from "src/auth/utils/refreshToken";
import { throwIfUnauthenticated } from "src/auth/utils/throwIfUnauthenticated";
import {
  clearCurrentUserTokens,
  getTokenForCurrentUser,
  hasCognitoToken,
  isCurrentUserTokenExpiringSoon,
  getPracticeIdsFromAccessToken,
  setTokens,
  setRefreshToken,
} from "src/auth/utils/userToken";
import { noop } from "src/common/utils";
import { convertQueryValueToString } from "src/common/utils/convertQueryValueToString";
import { client } from "src/common/utils/graphql";
import { logError } from "src/common/utils/reporting";
import { restoreVisitor } from "src/common/utils/restoreVisitor";
import { isBillingUser } from "src/graphql/utils/typeGuards";

import {
  AuthProviderDoctorsQuery,
  AuthProviderDoctorsQueryVariables,
} from "./__generated__/AuthProvider.type";
import {
  authProviderDefaultState,
  AuthProviderState,
  authProviderStoreReducer,
} from "./authProviderStore";
import useCognito, { UseCognitoData } from "./useCognito";

type AuthProviderProps = {
  onSignOut?: () => void;
};

type AuthContextCognitoData = Omit<UseCognitoData, "tryRestoreCognitoSession">;

export type AuthContextData = AuthProviderState &
  AuthContextCognitoData & {
    /**
     * Finalize sign in process by redirecting user into the app. This is separate call from
     * `signIn()` because signing-in could result in an half-way success state: "needTwoFactorAuth".
     * If called before user is fully authenticated, it will be a noop.
     */
    letUserIn: () => void;
    // should be only used on login flow
    selectPracticeOnLogin: (practiceId: string) => Promise<void>;
    cancelPracticeDoctorSelection: () => Promise<void>;
  };

export const AuthContext = createContext<AuthContextData | undefined>(undefined);

const authProviderDoctorsQuery = gql`
  query AuthProviderDoctors {
    me {
      ... on BillingUser {
        id
        allowedPractices {
          id
          name
        }
        myAccessibleDoctors {
          id
          practice {
            id
          }
        }
      }
    }
  }
`;

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function getPracticeIdFromPendingRedirect(): string | undefined {
  const oauthRedirect = peekPendingOAuthRedirectPath();
  const queryRedirect = convertQueryValueToString(Router.query.redirect);
  const resolved = resolvePostLoginRedirectPath({ queryRedirect, oauthRedirect });
  return extractPracticeIdFromPath(resolved) ?? undefined;
}

async function redirectUserToPageWithPracticePrefixIfNecessary(practiceId: string) {
  const { practiceId: practiceIdParam } = Router.query;
  if (
    practiceIdParam === undefined &&
    (Router.asPath.startsWith("/bills") ||
      Router.asPath.startsWith("/teams") ||
      Router.asPath.startsWith("/settings"))
  ) {
    await Router.replace(`/${practiceId}${Router.asPath}`);
  }
  return;
}

// Prefers a captured post-login redirect target (from the `redirect` query param on `/sign-in`,
// or from Cognito's OAuth customState) over the practiceId-prefix heuristic above. Used by every
// navigation site that runs once login completes, so they all agree on the same target.
// oauth redirect is for cognito login flow
// query redirect is for email-password login flow
async function redirectUserToResolvedTargetOrPracticePrefixed(practiceId: string) {
  const oauthRedirect = consumePendingOAuthRedirectPath();
  const queryRedirect = convertQueryValueToString(Router.query.redirect);
  const resolved = resolvePostLoginRedirectPath({ queryRedirect, oauthRedirect });
  if (resolved !== null) {
    await Router.replace(resolved);
    return;
  }
  await redirectUserToPageWithPracticePrefixIfNecessary(practiceId);
}

/**
 * Drops a captured post-login redirect so no later navigation follows it: clears the OAuth mailbox
 * and strips the `redirect` query param (a shallow replace, so the page isn't re-rendered).
 */
async function discardPendingRedirect() {
  consumePendingOAuthRedirectPath();
  if (Router.query.redirect === undefined) {
    return;
  }
  const query = { ...Router.query };
  delete query.redirect;
  await Router.replace({ pathname: Router.pathname, query }, undefined, { shallow: true });
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, onSignOut = noop }) => {
  const router = useRouter();
  const [state, dispatch] = useReducer(authProviderStoreReducer, authProviderDefaultState);

  const handleStartPracticeInitFlow = useCallback(
    async (props: {
      accessToken?: string;
      mountedRef: { mounted: boolean };
      restoringExistingSession: boolean;
      refreshToken?: string;
    }) => {
      await restoreVisitor();
      const { accessToken, mountedRef, restoringExistingSession } = props;
      if (!accessToken) {
        mountedRef.mounted &&
          dispatch({
            type: restoringExistingSession ? "GET_STORED_TOKEN_FAILED" : "LOGIN_FAILED",
            payload: restoringExistingSession
              ? undefined
              : { error: new Error("Authentication failed. No access token found.") },
          });
        return;
      }
      const decodedAccessToken = jwtDecode<AccessToken>(accessToken);
      const email = decodedAccessToken.email ?? "";
      const practiceIds = getPracticeIdsFromAccessToken(accessToken);
      if (practiceIds.length === 0) {
        mountedRef.mounted &&
          dispatch({
            type: "ERROR",
            payload: {
              error: new Error("Authentication failed. User is not assigned to any practice."),
            },
          });
        return;
      }

      if (email === undefined) {
        logError(new Error("Unable to get user info"), {
          customMessage: "Unable to get email from id token",
        });
        return;
      }

      const hasAccessToApp =
        decodedAccessToken.groups
          ?.split(",")
          ?.some(
            (group) =>
              group === Groups.RCM_ADMINS ||
              group === Groups.RCM_MEMBERS ||
              group === Groups.RCM_PRACTICE_ADMINS ||
              group === Groups.RCM_FRONT_DESK_STAFF ||
              group === Groups.RCM_OCC_HEALTH ||
              group === Groups.RCM_PRACTICE_STAFF ||
              group === Groups.RCM_PAYMENTS ||
              group === Groups.RCM_DME_BILLING ||
              group === Groups.RCM_VBC_BILLING
          ) ?? false;

      if (!hasAccessToApp) {
        mountedRef.mounted &&
          dispatch({
            type: "LOGIN_FAILED",
            payload: { error: new Error("User is not assigned to application.") },
          });
        return;
      }

      const { data } = await client.query<
        AuthProviderDoctorsQuery,
        AuthProviderDoctorsQueryVariables
      >({
        query: authProviderDoctorsQuery,
        context: { customAuthToken: accessToken },
      });

      const { practiceId: practiceIdParam } = Router.query;
      const maybeParsedPracticeId = convertQueryValueToString(practiceIdParam) ?? "";
      const parsedPracticeId = uuidRegex.test(maybeParsedPracticeId)
        ? maybeParsedPracticeId
        : undefined;
      // A post-login redirect like `/sign-in?redirect=/<practiceId>/bills/123` already names the
      // practice the user is heading to, so there is no need to ask them to pick one.
      const redirectPracticeId = getPracticeIdFromPendingRedirect();

      const me = isBillingUser(data?.me ?? undefined) ? throwIfUnauthenticated(data.me) : null;
      const userId = me?.id;

      // if user id is undefined, that means user has no accounts. They need to create one from the provider app
      // so, we can just open the practice selection
      if (userId === undefined) {
        mountedRef.mounted &&
          dispatch({
            type: "LOGIN_FAILED",
            payload: { error: new Error("Unable to get user info from the server") },
          });
        return;
      }

      // if user has no practices, we can't let them in
      if (me?.allowedPractices?.length === 0) {
        mountedRef.mounted &&
          dispatch({
            type: "LOGIN_FAILED",
            payload: { error: new Error("User is not assigned to a practice") },
          });
        return;
      }

      // if user has no doctors, we need to show the practice doctor selector
      if (me?.myAccessibleDoctors?.length === 0) {
        // let user set up a doctor
        mountedRef.mounted &&
          dispatch({
            type: "PRACTICE_DOCTOR_SELECTION_NEEDED",
            payload: {
              practiceDoctorSelectorUserId: userId,
              accessToken,
              refreshToken: props.refreshToken,
            },
          });
        return;
      }

      // if there is a practice ID in the URL and the user does not have access to it,
      // redirect user to practice selection and notify user that they do not have access to that practice.
      const hasAccessToParsedPractice =
        parsedPracticeId !== undefined
          ? me?.myAccessibleDoctors?.some((doctor) => doctor?.practice?.id === parsedPracticeId)
          : false;

      if (parsedPracticeId !== undefined && !hasAccessToParsedPractice) {
        mountedRef.mounted &&
          dispatch({
            type: "PRACTICE_DOCTOR_SELECTION_NEEDED",
            payload: {
              practiceDoctorSelectorUserId: userId,
              accessToken,
              refreshToken: props.refreshToken,
              error: new Error(
                `User does not have access to practice with id "${parsedPracticeId}"`
              ),
            },
          });
        return;
      }

      // A redirect is only followed when it has a practice the user has a permission
      const hasAccessToRedirectPractice =
        redirectPracticeId !== undefined
          ? me?.myAccessibleDoctors?.some(
              (doctor) => doctor?.practice?.id === redirectPracticeId
            ) ?? false
          : false;

      if (!hasAccessToRedirectPractice) {
        await discardPendingRedirect();
      }

      // if user has only one practice and one doctor, we can let them in automatically
      if (me?.myAccessibleDoctors?.length === 1 && me?.allowedPractices?.length === 1) {
        const practiceId = me?.myAccessibleDoctors?.[0]?.practice?.id ?? undefined;

        if (practiceId === undefined) {
          mountedRef.mounted &&
            dispatch({
              type: "LOGIN_FAILED",
              payload: { error: new Error("Could not find practice ID.") },
            });
          return;
        }

        await setStoredPracticeId(practiceId, userId);
        setTokens(userId, accessToken);
        if (props.refreshToken) {
          setRefreshToken(userId, props.refreshToken);
        }
        await redirectUserToResolvedTargetOrPracticePrefixed(practiceId);
        await restoreVisitor();
        mountedRef.mounted && dispatch({ type: "LOGIN_DONE", payload: { userId } });
        return;
      }

      // if user's selected practice is not in the list of practices they have access to,
      const storedPracticeId = await getStoredPracticeId();
      const hasAccessToStoredPractice =
        me?.myAccessibleDoctors?.some((doctor) => doctor?.practice?.id === storedPracticeId) ??
        false;

      // on page refresh/load, if user has already logged in to a practice, let them in with that practice
      if (restoringExistingSession && storedPracticeId !== null && hasAccessToStoredPractice) {
        setTokens(userId, accessToken);
        if (props.refreshToken) {
          setRefreshToken(userId, props.refreshToken);
        }
        await setStoredPracticeId(storedPracticeId, userId);

        // if there's a practice ID in the URL, and it's different from the stored practice ID, and user has access to it
        // set the local practice ID to the parsed practice ID
        if (
          parsedPracticeId !== undefined &&
          parsedPracticeId !== storedPracticeId &&
          hasAccessToParsedPractice
        ) {
          setLocalPracticeId(parsedPracticeId);
          await redirectUserToResolvedTargetOrPracticePrefixed(parsedPracticeId);
        } else {
          await redirectUserToResolvedTargetOrPracticePrefixed(storedPracticeId);
        }

        await restoreVisitor();
        mountedRef.mounted && dispatch({ type: "LOGIN_DONE", payload: { userId } });
      } else if (redirectPracticeId !== undefined && hasAccessToRedirectPractice) {
        // The user is on their way to a practice-prefixed page they can access, so there is nothing
        // for them to pick here: log them into that practice and send them along.
        setTokens(userId, accessToken);
        if (props.refreshToken) {
          setRefreshToken(userId, props.refreshToken);
        }
        await setStoredPracticeId(redirectPracticeId, userId);
        await redirectUserToResolvedTargetOrPracticePrefixed(redirectPracticeId);
        await restoreVisitor();
        mountedRef.mounted && dispatch({ type: "LOGIN_DONE", payload: { userId } });
      } else {
        // show the practice doctor selector
        mountedRef.mounted &&
          dispatch({
            type: "PRACTICE_DOCTOR_SELECTION_NEEDED",
            payload: {
              practiceDoctorSelectorUserId: userId,
              accessToken,
              refreshToken: props.refreshToken,
            },
          });
      }
    },
    []
  );

  const selectPracticeOnLogin = useCallback(
    async (practiceId: string) => {
      if (state.practiceDoctorSelectorUserId === undefined) {
        dispatch({
          type: "PRACTICE_DOCTOR_SELECTION_FAILED",
          payload: {
            error: new Error("User ID is not found."),
          },
        });
        return;
      }

      await client.clearStore();
      await setStoredPracticeId(practiceId, state.practiceDoctorSelectorUserId);
      await handleStartPracticeInitFlow({
        restoringExistingSession: true,
        mountedRef: { mounted: true },
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }).catch((error) => {
        dispatch({ type: "ERROR", payload: { error } });
      });
    },
    [
      state.accessToken,
      state.refreshToken,
      state.practiceDoctorSelectorUserId,
      handleStartPracticeInitFlow,
    ]
  );

  const { tryRestoreCognitoSession, ...cognitoHelpers } = useCognito({
    dispatch,
    onStartPracticeInitFlow: handleStartPracticeInitFlow,
    onSignOut,
  });

  const cancelPracticeDoctorSelection = useCallback(async () => {
    // Discard any captured OAuth redirect path so it can't leak into a later, unrelated login
    // attempt if the user backs out of practice/doctor selection.
    consumePendingOAuthRedirectPath();
    await clearCurrentUserTokens();
    onSignOut();
    dispatch({ type: "2FA_CANCELLED" });
  }, [onSignOut]);

  // Check if the user is already authenticated on page load, if so, restore the session or flows
  useEffect(() => {
    let mounted = true;

    async function restoreSession() {
      const token = getTokenForCurrentUser();
      if (token === null || token === "") {
        // No token found, user is not authenticated
        dispatch({ type: "GET_STORED_TOKEN_FAILED" });
        return;
      }

      if (isCurrentUserTokenExpiringSoon()) {
        try {
          // if token is expiring soon, refresh it
          const token = await refreshToken();
          if (!token) {
            // if token is not refreshed, user is not authenticated
            mounted && dispatch({ type: "GET_STORED_TOKEN_FAILED" });
            return;
          }
        } catch {
          mounted && dispatch({ type: "GET_STORED_TOKEN_FAILED" });
          return;
        }
      }

      if (hasCognitoToken()) {
        await tryRestoreCognitoSession();
        return;
      }

      // no branch executed, user is not authenticated
      dispatch({ type: "GET_STORED_TOKEN_FAILED" });
      return;
    }

    restoreSession().catch((error) => mounted && dispatch({ type: "ERROR", payload: { error } }));

    return () => {
      mounted = false;
    };
  }, [tryRestoreCognitoSession]);

  useEffect(() => {
    if (state.status === "unauthenticated") {
      clearCurrentUserTokens();
    }
  }, [state.status]);

  const letUserIn = useCallback(async () => {
    if (state.status !== "authenticated") return;
    const queryRedirect = convertQueryValueToString(router.query.redirect);
    const redirectPath = isSafeInternalRedirectPath(queryRedirect) ? queryRedirect : null;
    // redirect user to redirect path on email-pass login flow, or the home page otherwise
    await router.replace(redirectPath ?? "/");
  }, [router, state.status]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        letUserIn,
        cancelPracticeDoctorSelection,
        selectPracticeOnLogin,
        ...cognitoHelpers,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
